import * as SentryNext from '@sentry/nextjs'
import type { BrowserOptions, EdgeOptions, NodeOptions } from '@sentry/nextjs'
import {
  browserTracingIntegration,
  browserProfilingIntegration,
  feedbackIntegration,
  launchDarklyIntegration as browserLaunchDarklyIntegration,
  replayIntegration,
  consoleLoggingIntegration as browserConsoleLoggingIntegration,
  buildLaunchDarklyFlagUsedHandler,
  graphqlClientIntegration,
} from '@sentry/browser'
import { resolveHttpGraphqlUrl } from '../graphqlEndpoint'
import { normalizeFrontendTransactionEvent } from './frontendViews'
import {
  emitBrowserTraceMetrics,
  type BrowserTransactionEvent,
} from './traceMetrics'
import { buildInitialSentryScope } from './userIdentity'

const DEFAULT_TRACE_SAMPLE_RATE = 0.01
const DEFAULT_PROFILE_SAMPLE_RATE = 0
// Self-hosted Sentry DSN - configure via NEXT_PUBLIC_SENTRY_DSN environment variable.
// The canonical UI and ingest host is sentry.trueloyalty.ai.
// Format: https://<key>@sentry.trueloyalty.ai/<project_id>
// NOTE: DSN must be obtained from the self-hosted Sentry instance after project creation
const DEFAULT_SENTRY_DSN = ''

const TRACE_PROPAGATION_TARGETS: (string | RegExp)[] = [
  // Localhost patterns
  /^https?:\/\/localhost(:\d+)?/,
  /^\/api\//,
  /^\/graphql$/,
  // Production domains
  /^https?:\/\/[^/]*\.?trueloyalty\.ai/,
  // Relative URLs
  /^\//,
]

const coerceNumber = (value: string | undefined, fallback: number) => {
  if (!value) return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

type FrontendTransactionEvent = {
  type?: string
  transaction?: string
  contexts?: {
    trace?: {
      op?: string
      span_id?: string
      trace_id?: string
    }
  }
  tags?: Record<string, unknown>
  transaction_info?: Record<string, unknown>
  event_id?: string
  timestamp?: number | string
}

const getRelease = () =>
  process.env.SENTRY_RELEASE || process.env.NEXT_PUBLIC_SENTRY_RELEASE

const getDsn = () =>
  process.env.NEXT_PUBLIC_SENTRY_DSN ||
  process.env.SENTRY_DSN ||
  DEFAULT_SENTRY_DSN

const tracesSampler = (rate: number) => (samplingContext: Parameters<NonNullable<BrowserOptions['tracesSampler']>>[0]) => {
  if (samplingContext.parentSampled) return 1
  const forceSample = samplingContext.customSamplingContext?.forceSample === true
  if (forceSample) return 1
  const name = samplingContext.transactionContext?.name || ''
  if (name.includes('error') || name.includes('Exception')) return 1
  if (name.startsWith('Frontend view: ')) {
    return coerceNumber(
      process.env.NEXT_PUBLIC_SENTRY_FRONTEND_VIEW_SAMPLE_RATE,
      1
    )
  }
  return rate
}

const buildBrowserIntegrations = (
  integrations: any[],
  graphqlEndpoint: string | undefined
) => {
  const base = integrations.filter((integration) => {
    const name = integration.name
    return name !== 'BrowserTracing' && name !== 'Feedback' && name !== 'Console'
  })

  base.push(
    browserTracingIntegration({
      enableInp: true,
      enableLongTask: true,
      enableLongAnimationFrame: true,
      idleTimeout: 5000,
    })
  )
  base.push(browserProfilingIntegration())

  const feedbackTheme = {
    foreground: '#FFFFFF',
    background: '#0A0A0A',
    accentForeground: '#FFFFFF',
    accentBackground: '#242B61',
    successColor: '#24B57A',
    errorColor: '#F15A5A',
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0px 16px 40px rgba(0,0,0,0.55)',
    outline: 'none',
    interactiveFilter: 'brightness(110%)',
  }

  const isBrowser = typeof window !== 'undefined'

  if (isBrowser) {
    const flagInspector = buildLaunchDarklyFlagUsedHandler()
    base.push(
      browserLaunchDarklyIntegration()
    )
    base.push(
      replayIntegration({
        networkDetailAllowUrls: graphqlEndpoint ? [graphqlEndpoint] : undefined,
        blockAllMedia: false,
        maskAllText: false,
      })
    )

    base.push(
      feedbackIntegration({
        colorScheme: 'system',
        autoInject: false,
        showBranding: false,
        triggerLabel: 'Report a Bug',
        formTitle: 'Report a Bug',
        submitButtonLabel: 'Send Report',
        cancelButtonLabel: 'Cancel',
        themeLight: feedbackTheme,
        themeDark: feedbackTheme,
      })
    )
  }

  base.push(
    browserConsoleLoggingIntegration({
      levels: ['log', 'warn', 'error'],
    })
  )

  if (typeof graphqlClientIntegration === 'function') {
    base.push(graphqlClientIntegration({ endpoints: graphqlEndpoint ? [graphqlEndpoint] : [] }))
  }

  return base
}

const buildServerIntegrations = (integrations: any[]) => {
  const base = integrations.filter((integration) => integration.name !== 'Console')

  base.push(
    SentryNext.consoleLoggingIntegration({
      levels: ['log', 'warn', 'error'],
    })
  )

  return base
}

const enableLogs = () => process.env.SENTRY_ENABLE_LOGS !== '0'

type BrowserSentryOptions = BrowserOptions & {
  _experiments?: {
    enableMetrics?: boolean
  }
  profilesSampleRate?: number
}

const createBrowserOptions = (): BrowserSentryOptions => {
  const rate = coerceNumber(
    process.env.NEXT_PUBLIC_SENTRY_TRACE_SAMPLE_RATE,
    DEFAULT_TRACE_SAMPLE_RATE
  )
  const graphqlEndpoint = resolveHttpGraphqlUrl()

  return {
    dsn: getDsn(),
    release: getRelease(),
    environment: process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV,
    enableLogs: process.env.NEXT_PUBLIC_SENTRY_ENABLE_LOGS !== '0',
    _experiments: {
      enableMetrics: true,
    },
    initialScope: buildInitialSentryScope(),
    sendDefaultPii: false,
    replaysSessionSampleRate: coerceNumber(
      process.env.NEXT_PUBLIC_SENTRY_REPLAY_SAMPLE_RATE,
      0
    ),
    replaysOnErrorSampleRate: 1,
    profilesSampleRate: coerceNumber(
      process.env.NEXT_PUBLIC_SENTRY_PROFILE_SAMPLE_RATE,
      DEFAULT_PROFILE_SAMPLE_RATE
    ),
    tracesSampler: tracesSampler(rate),
    debug: process.env.SENTRY_DEBUG === '1',
    tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
    beforeSendTransaction: (event) => {
      const normalizedEvent = normalizeFrontendTransactionEvent(
        event as BrowserTransactionEvent
      )
      emitBrowserTraceMetrics(normalizedEvent)
      return normalizedEvent as typeof event
    },
    integrations: (integrations) =>
      buildBrowserIntegrations(integrations, graphqlEndpoint),
  }
}

const createServerOptions = (): NodeOptions => {
  const rate = coerceNumber(
    process.env.SENTRY_TRACES_SAMPLE_RATE || process.env.SENTRY_TRACE_SAMPLE_RATE,
    DEFAULT_TRACE_SAMPLE_RATE
  )

  return {
    dsn: getDsn(),
    release: getRelease(),
    environment: process.env.APP_ENV || process.env.NODE_ENV,
    enableLogs: enableLogs(),
    sendDefaultPii: true,
    tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
    tracesSampler: tracesSampler(rate),
    debug: process.env.SENTRY_DEBUG === '1',
    integrations: (integrations) => buildServerIntegrations(integrations),
  }
}

const createEdgeOptions = (): EdgeOptions => {
  const rate = coerceNumber(
    process.env.SENTRY_TRACES_SAMPLE_RATE || process.env.SENTRY_TRACE_SAMPLE_RATE,
    DEFAULT_TRACE_SAMPLE_RATE
  )

  return {
    dsn: getDsn(),
    release: getRelease(),
    environment:
      process.env.NEXT_PUBLIC_APP_ENV ||
      process.env.APP_ENV ||
      process.env.NODE_ENV,
    enableLogs: enableLogs(),
    sendDefaultPii: true,
    tracesSampler: tracesSampler(rate),
    debug: process.env.SENTRY_DEBUG === '1',
    tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
    integrations: (integrations) => buildServerIntegrations(integrations),
  }
}

let hasInitialized = false

const initOnce = (options: BrowserOptions | NodeOptions | EdgeOptions) => {
  if (hasInitialized) {
    return
  }
  SentryNext.init(options)
  hasInitialized = true
}

export const initSentryBrowser = () => initOnce(createBrowserOptions())
export const initSentryServer = () => initOnce(createServerOptions())
export const initSentryEdge = () => initOnce(createEdgeOptions())
